import React, { useEffect, useRef, useState } from 'react';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';

import { Spinner } from 'react-bootstrap';

import { useSelector, useDispatch } from 'react-redux';
import { createReport,createWaterReport } from '../../store/features/createReportSlice';

const reportSchema = Yup.object({
  sample_id: Yup.string().required('Sample ID is required'),
  ph: Yup.string().required('PH Value is required'),
  ec: Yup.string().required('EC Value is required'),
  oc: Yup.string().required('OC Value is required'),
  nitrate: Yup.string().required('Nitrate Value is required'),
  ammonia: Yup.string().required('Ammonia Value is required'),
  phosphorus: Yup.string().required('Phosphorus Value is required'),
  potassium: Yup.string().required('Potassium Value is required'),
});

const waterReportSchema = Yup.object({
  sample_id: Yup.string().required('Sample ID is required'),
  carbonate: Yup.string().required('Carbonate Value is required'),
  bicarbonate: Yup.string().required('Bicarbonate Value is required'),
  tds: Yup.string().required('TDS Value is required'),
});

const generateClassName = (errors, touched, fieldName) => {
  const hasError = errors[fieldName] && touched[fieldName];
  const isSuccess = touched[fieldName];
  return `form-control ${
    hasError ? 'border-2 border-danger' : isSuccess && 'border-2 border-success'
  }`;
};

const NumberField = ({ field, form, ...props }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    const input = inputRef.current;

    const handleWheel = (event) => {
      if (document.activeElement === input) {
        event.preventDefault();
      }
    };

    input.addEventListener('wheel', handleWheel);

    return () => {
      input.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return <input ref={inputRef} type='number' {...field} {...props} />;
};

function ReportForm() {
  const [reportType, setReportType] = useState('soil');
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.createReport);

  const soilInitialValues = {
    sample_id: '',
    ph: '',
    ec: '',
    oc: '',
    nitrate: '',
    ammonia: '',
    phosphorus: '',
    potassium: '',
    calcium: '',
    magnesium: '',
    sulphur: '',
    boron: '',
    iron: '',
    copper: '',
    zinc: '',
  };

  const waterInitialValues = {
    sample_id: '',
    ph: '',
    ec: '',
    potassium: '',
    phosphorus: '',
    ammonia: '',
    nitrate: '',
    sulphur: '',
    boron: '',
    zinc: '',
    iron: '',
    calcium: '',
    magnesium: '',
    copper: '',
    carbonate: '',
    bicarbonate: '',
    tds: '',
  };

  const currentSchema =
  reportType === 'soil' ? reportSchema : waterReportSchema;

  const schemaDescription = currentSchema.describe();

  const isRequired = (field) =>
    schemaDescription.fields[field]?.tests?.some(
      (test) => test.name === 'required'
    );

  const renderLabel = (field, label) => (
    <Form.Label htmlFor={field}>
      {label}
      {isRequired(field) && (
        <span className="text-danger">&nbsp;*</span>
      )}
    </Form.Label>
  );
  
  return (
    <Formik
      enableReinitialize
      initialValues={
        reportType === 'soil'
          ? soilInitialValues
          : waterInitialValues
      }
      
      validationSchema={
        reportType === 'soil' ? reportSchema : waterReportSchema
      }
      onSubmit={(values, { resetForm }) => {
        dispatch(
          reportType === 'soil'
            ? createReport(values)
            : createWaterReport(values)
        ).then(() => resetForm())
          .catch((err) => {
            console.log('Error while creating report: ', err);
          });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
      }) => (
        <div className='p-4'>
          <Form
            className='form__details'
            onSubmit={handleSubmit}
          >
            <h3 className='pt-4 pb-0'>Report generation Form</h3>
            <div className="d-flex gap-3 my-3">
            <Form.Check
              type="radio"
              label="Soil Report"
              checked={reportType === 'soil'}
              onChange={() => setReportType('soil')}
            />
            <Form.Check
              type="radio"
              label="Water Report"
              checked={reportType === 'water'}
              onChange={() => setReportType('water')}
            />
            </div>
            <Row className='my-4'>
              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    type='text'
                    name='sample_id'
                    value={values.sample_id}
                    placeholder='Enter Sample ID'
                    className={generateClassName(errors, touched, 'sample_id')}
                    onChange={handleChange}
                  />
                  {renderLabel('sample_id', 'Sample ID')}
                </div>
                {errors.sample_id && touched.sample_id ? (
                  <div className='form_errors'> &#9888; {errors.sample_id}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='ph'
                    value={values.ph}
                    placeholder='Enter PH'
                    className={generateClassName(errors, touched, 'ph')}
                    onChange={handleChange}
                  />
                  {renderLabel('ph', 'PH')}
                </div>
                {errors.ph && touched.ph ? (
                  <div className='form_errors'> &#9888; {errors.ph}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='ec'
                    value={values.ec}
                    placeholder='Enter EC'
                    className={generateClassName(errors, touched, 'ec')}
                    onChange={handleChange}
                  />
                  {renderLabel('ec', 'EC')}
                </div>
                {errors.ec && touched.ec ? (
                  <div className='form_errors'> &#9888; {errors.ec}</div>
                ) : null}
              </Form.Group>

              {reportType === 'soil' && (
                <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='oc'
                    value={values.oc}
                    placeholder='Enter OC'
                    className={generateClassName(errors, touched, 'oc')}
                    onChange={handleChange}
                  />
                  {renderLabel('oc', 'OC')}
                </div>
                {errors.oc && touched.oc ? (
                  <div className='form_errors'> &#9888; {errors.oc}</div>
                ) : null}
              </Form.Group>
              )}

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='nitrate'
                    value={values.nitrate}
                    placeholder='Enter Nitrate'
                    className={generateClassName(errors, touched, 'nitrate')}
                    onChange={handleChange}
                  />
                  {renderLabel('nitrate', 'Nitrate')}
                </div>
                {errors.nitrate && touched.nitrate ? (
                  <div className='form_errors'> &#9888; {errors.nitrate}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='ammonia'
                    value={values.ammonia}
                    placeholder='Enter Ammonia'
                    className={generateClassName(errors, touched, 'ammonia')}
                    onChange={handleChange}
                  />
                  {renderLabel('ammonia', 'Ammonia')}
                </div>
                {errors.ammonia && touched.ammonia ? (
                  <div className='form_errors'> &#9888; {errors.ammonia}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='phosphorus'
                    value={values.phosphorus}
                    placeholder='Enter Phosphorus'
                    className={generateClassName(errors, touched, 'phosphorus')}
                    onChange={handleChange}
                  />
                  {renderLabel('phosphorus', 'Phosphorus')}
                </div>
                {errors.phosphorus && touched.phosphorus ? (
                  <div className='form_errors'>
                    {' '}
                    &#9888; {errors.phosphorus}
                  </div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='potassium'
                    value={values.potassium}
                    placeholder='Enter Potassium'
                    className={generateClassName(errors, touched, 'potassium')}
                    onChange={handleChange}
                  />
                  {renderLabel('potassium', 'Potassium')}
                </div>
                {errors.potassium && touched.potassium ? (
                  <div className='form_errors'> &#9888; {errors.potassium}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='calcium'
                    value={values.calcium}
                    placeholder='Enter Calcium'
                    className={generateClassName(errors, touched, 'calcium')}
                    onChange={handleChange}
                  />
                  {renderLabel('calcium', 'Calcium')}
                </div>
                {errors.calcium && touched.calcium ? (
                  <div className='form_errors'> &#9888; {errors.calcium}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='magnesium'
                    value={values.magnesium}
                    placeholder='Enter Magnesium'
                    className={generateClassName(errors, touched, 'magnesium')}
                    onChange={handleChange}
                  />
                  {renderLabel('magnesium', 'Magnesium')}
                </div>
                {errors.magnesium && touched.magnesium ? (
                  <div className='form_errors'> &#9888; {errors.magnesium}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='sulphur'
                    value={values.sulphur}
                    placeholder='Enter Sulphur'
                    className={generateClassName(errors, touched, 'sulphur')}
                    onChange={handleChange}
                  />
                  {renderLabel('sulphur', 'Sulphur')}
                </div>
                {errors.sulphur && touched.sulphur ? (
                  <div className='form_errors'> &#9888; {errors.sulphur}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='boron'
                    value={values.boron}
                    placeholder='Enter Boron'
                    className={generateClassName(errors, touched, 'boron')}
                    onChange={handleChange}
                  />
                  {renderLabel('boron', 'Boron')}
                </div>
                {errors.boron && touched.boron ? (
                  <div className='form_errors'> &#9888; {errors.boron}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='iron'
                    value={values.iron}
                    placeholder='Enter Iron'
                    className={generateClassName(errors, touched, 'iron')}
                    onChange={handleChange}
                  />
                  {renderLabel('iron', 'Iron')}
                </div>
                {errors.iron && touched.iron ? (
                  <div className='form_errors'> &#9888; {errors.iron}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='zinc'
                    value={values.zinc}
                    placeholder='Enter Zinc'
                    className={generateClassName(errors, touched, 'zinc')}
                    onChange={handleChange}
                  />
                  {renderLabel('zinc', 'Zinc')}
                </div>
                {errors.zinc && touched.zinc ? (
                  <div className='form_errors'> &#9888; {errors.zinc}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='copper'
                    value={values.copper}
                    placeholder='Enter copper'
                    className={generateClassName(errors, touched, 'copper')}
                    onChange={handleChange}
                  />
                  {renderLabel('copper', 'Copper')}
                </div>
                {errors.copper && touched.copper ? (
                  <div className='form_errors'> &#9888; {errors.copper}</div>
                ) : null}
              </Form.Group>

              {reportType === 'water' && (
                <>
                <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='carbonate'
                    value={values.carbonate}
                    placeholder='Enter Carbonate'
                    className={generateClassName(errors, touched, 'carbonate')}
                    onChange={handleChange}
                  />
                  {renderLabel('carbonate', 'Carbonate')}
                </div>
                {errors.carbonate && touched.carbonate ? (
                  <div className='form_errors'> &#9888; {errors.carbonate}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='bicarbonate'
                    value={values.bicarbonate}
                    placeholder='Enter bicarbonate'
                    className={generateClassName(errors, touched, 'bicarbonate')}
                    onChange={handleChange}
                  />
                  {renderLabel('bicarbonate', 'Bicarbonate')}
                </div>
                {errors.bicarbonate && touched.bicarbonate ? (
                  <div className='form_errors'> &#9888; {errors.bicarbonate}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='tds'
                    value={values.tds}
                    placeholder='Enter TDS'
                    className={generateClassName(errors, touched, 'tds')}
                    onChange={handleChange}
                  />
                  {renderLabel('tds', 'TDS')}
                </div>
                {errors.tds && touched.tds ? (
                  <div className='form_errors'> &#9888; {errors.tds}</div>
                ) : null}
              </Form.Group>
              </>
              )}
            </Row>

            <div className='my-3 col col-md-6 mx-auto'>
              <button
                type='submit'
                className='w-100 text-center btn btn-dark'
                disabled={status === 'loading'}
              >
                {status === 'loading' && (
                  <Spinner
                    as='span'
                    animation='border'
                    size='sm'
                    role='status'
                    aria-hidden='true'
                  />
                )}
                <span>Submit</span>
              </button>
            </div>
          </Form>
        </div>
      )}
    </Formik>
  );
}

export default ReportForm;
